import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useControllableState } from '../../hooks/useControllableState.js';
import { useFloating } from '../../hooks/useFloating.js';
import { IcnChevron, IcnClose, IcnPlus } from '../../icons/index.js';
import { useI18nT } from '../../i18n/index.js';
import { useMobileFloatingSheet } from '../shared/mobileFloating.js';
import { ensurePortalRoot, matchQuery, optionLabelText, optionSearchText } from '../Select/utils.js';
import { SelectTagPanel } from './SelectTagPanel.js';
import type { SelectTagOption, SelectTagProps, SelectTagRow } from './types.js';
import {
  customValueFromQuery,
  firstEnabledRow,
  nextEnabledRow,
  optionFromValue,
  uniqueValues,
} from './utils.js';

const PANEL_MAX_HEIGHT = 360;

export function SelectTag<V extends string = string>(props: SelectTagProps<V>): ReactNode {
  const t = useI18nT();
  const {
    options,
    value,
    defaultValue = [],
    onChange,
    placeholder = t('selectTag.placeholder'),
    disabled = false,
    invalid = false,
    name,
    size = 'md',
    className,
    id,
    ref,
    maxTags,
    searchable = false,
    searchPlaceholder = t('select.searchPlaceholder'),
    filter = 'contains',
    filterFn,
    allowCustomValue = false,
    normalizeCustomValue,
    renderCustomOption,
    renderEmpty,
    renderTag,
    removeLabel,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  } = props;

  const [selected, setSelectedRaw] = useControllableState<readonly V[]>({
    defaultValue,
    ...(value !== undefined ? { value } : {}),
    ...(onChange !== undefined ? { onChange } : {}),
  });
  const selectedValues = useMemo(() => uniqueValues(selected), [selected]);
  const selectedSet = useMemo(() => new Set<V>(selectedValues), [selectedValues]);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const reactId = useId();
  const rootId = id ?? reactId;
  const triggerId = `${rootId}-trigger`;
  const panelId = `${rootId}-panel`;

  const {
    setAnchor,
    setFloating,
    rect: floatingRect,
    floatingStyle,
    maxHeight: floatingMaxHeight,
    anchorVisible,
    update: updateFloating,
  } = useFloating({
    open,
    placement: 'bottom-start',
    gap: 6,
    padding: 8,
    matchAnchorWidth: true,
    hideWhenDetached: true,
  });
  const mobileSheet = useMobileFloatingSheet(open);

  const optionByValue = useMemo(() => {
    const map = new Map<V, SelectTagOption<V>>();
    for (const option of options) map.set(option.value, option);
    return map;
  }, [options]);

  const selectedOptions = useMemo(
    () => selectedValues.map((item) => optionByValue.get(item) ?? optionFromValue(item)),
    [optionByValue, selectedValues],
  );

  const rows = useMemo(() => {
    const atCap = maxTags !== undefined && selectedValues.length >= maxTags;
    const next: SelectTagRow<V>[] = [];
    for (const option of options) {
      const matched =
        filterFn !== undefined
          ? filterFn(option, query)
          : matchQuery(optionSearchText(option), query, filter);
      if (!matched) continue;
      const selectedOption = selectedSet.has(option.value);
      next.push({
        key: option.value,
        value: option.value,
        option,
        selected: selectedOption,
        disabled: option.disabled === true || (!selectedOption && atCap),
        custom: false,
      });
    }

    const customValue = customValueFromQuery(query, allowCustomValue, normalizeCustomValue);
    if (customValue !== null && !optionByValue.has(customValue) && !selectedSet.has(customValue)) {
      next.push({
        key: `__custom__${customValue}`,
        value: customValue,
        option: {
          value: customValue,
          label:
            renderCustomOption !== undefined
              ? renderCustomOption(customValue, query)
              : t('selectTag.createOption', { value: customValue }),
          searchText: customValue,
        },
        selected: false,
        disabled: atCap,
        custom: true,
      });
    }
    return next;
  }, [
    allowCustomValue,
    filter,
    filterFn,
    maxTags,
    normalizeCustomValue,
    optionByValue,
    options,
    query,
    renderCustomOption,
    selectedSet,
    selectedValues.length,
    t,
  ]);

  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      setAnchor(node);
      if (typeof ref === 'function') ref(node);
      else if (ref !== undefined && ref !== null) {
        (ref as { current: HTMLButtonElement | null }).current = node;
      }
    },
    [ref, setAnchor],
  );

  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      setFloating(node);
    },
    [setFloating],
  );

  const setSelected = useCallback(
    (next: readonly V[]): void => {
      setSelectedRaw(uniqueValues(next));
    },
    [setSelectedRaw],
  );

  const closePanel = useCallback((returnFocus: boolean): void => {
    setOpen(false);
    setQuery('');
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  const openPanel = useCallback((): void => {
    if (disabled) return;
    setOpen(true);
    setHighlightIndex((current) => firstEnabledRow(rows, current));
  }, [disabled, rows]);

  const toggleRow = useCallback(
    (row: SelectTagRow<V>): void => {
      if (row.disabled) return;
      if (selectedSet.has(row.value)) {
        setSelected(selectedValues.filter((item) => item !== row.value));
      } else {
        setSelected([...selectedValues, row.value]);
      }
      setQuery('');
      setHighlightIndex(0);
    },
    [selectedSet, selectedValues, setSelected],
  );

  const removeValue = useCallback(
    (item: V): void => {
      setSelected(selectedValues.filter((valueItem) => valueItem !== item));
    },
    [selectedValues, setSelected],
  );

  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current?.contains(target) === true) return;
      if (panelRef.current?.contains(target) === true) return;
      closePanel(false);
    };
    document.addEventListener('mousedown', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
    };
  }, [open, closePanel]);

  useEffect(() => {
    if (mobileSheet) return undefined;
    if (!open) return undefined;
    const handler = (event: Event): void => {
      const target = event.target;
      if (target instanceof Node && panelRef.current?.contains(target) === true) return;
      closePanel(false);
    };
    window.addEventListener('scroll', handler, true);
    document.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('scroll', handler, true);
      document.removeEventListener('scroll', handler, true);
    };
  }, [open, mobileSheet, closePanel]);

  useEffect(() => {
    if (open && !mobileSheet && !anchorVisible) closePanel(false);
  }, [open, mobileSheet, anchorVisible, closePanel]);

  useEffect(() => {
    if (!open || !searchable) return;
    searchInputRef.current?.focus();
  }, [open, searchable]);

  useLayoutEffect(() => {
    if (!open) return;
    updateFloating();
  }, [open, query, rows.length, selectedValues.length, updateFloating]);

  useEffect(() => {
    if (!open) return;
    setHighlightIndex((current) => firstEnabledRow(rows, current));
  }, [open, rows]);

  const onTriggerClick = (): void => {
    if (disabled) return;
    if (open) closePanel(false);
    else openPanel();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>): void => {
    if (disabled) return;
    if (!open) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        openPanel();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel(true);
      return;
    }
    if (event.key === 'Tab') {
      closePanel(false);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightIndex((current) => nextEnabledRow(rows, current, event.key === 'ArrowDown' ? 1 : -1));
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setHighlightIndex(event.key === 'Home' ? nextEnabledRow(rows, -1, 1) : nextEnabledRow(rows, rows.length, -1));
      return;
    }
    if (event.key === 'Enter' || (event.key === ' ' && event.target !== searchInputRef.current)) {
      const row = rows[highlightIndex];
      if (row !== undefined) {
        event.preventDefault();
        toggleRow(row);
      }
    }
  };

  const portalRoot = ensurePortalRoot();
  const panelMaxHeight =
    floatingMaxHeight > 0 ? Math.min(PANEL_MAX_HEIGHT, floatingMaxHeight) : PANEL_MAX_HEIGHT;
  const rootClasses = ['vq-select-tag', className].filter(Boolean).join(' ');
  const stateAttr = disabled ? 'disabled' : invalid ? 'invalid' : open ? 'open' : 'idle';
  const selectedCountLabel = t('selectTag.selectedCount', { count: selectedValues.length });

  return (
    <div className={rootClasses} data-component="SelectTag" data-size={size} data-state={stateAttr}>
      <div className="vq-select-tag__control" data-slot="control">
        <div className="vq-select-tag__values" data-slot="values">
          {selectedOptions.length > 0 ? (
            selectedOptions.map((option) => (
              <span key={option.value} className="vq-select-tag__chip" data-slot="tag">
                <span className="vq-select-tag__chip-label">
                  {renderTag !== undefined ? renderTag(option, option.value) : option.label}
                </span>
                <button
                  type="button"
                  className="vq-select-tag__chip-remove"
                  aria-label={
                    removeLabel !== undefined
                      ? removeLabel(option, option.value)
                      : t('selectTag.removeLabel', { label: optionLabelText(option.label) || option.value })
                  }
                  disabled={disabled}
                  onClick={(event) => {
                    event.stopPropagation();
                    removeValue(option.value);
                  }}
                >
                  <IcnClose size={12} />
                </button>
              </span>
            ))
          ) : (
            <span className="vq-select-tag__placeholder">{placeholder}</span>
          )}
        </div>
        <button
          ref={setTriggerRef}
          id={triggerId}
          type="button"
          className="vq-select-tag__trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? panelId : undefined}
          aria-label={ariaLabel ?? selectedCountLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-disabled={disabled || undefined}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          onClick={onTriggerClick}
          onKeyDown={onKeyDown}
        >
          <IcnPlus size={13} />
          <span>{selectedOptions.length > 0 ? t('selectTag.add') : placeholder}</span>
          <span className="vq-select-tag__chevron" aria-hidden="true">
            <IcnChevron size={12} />
          </span>
        </button>
      </div>
      {name !== undefined ? <input type="hidden" name={name} value={selectedValues.join(',')} /> : null}
      {open && portalRoot !== null
        ? createPortal(
            <SelectTagPanel
              panelId={panelId}
              triggerId={triggerId}
              rows={rows}
              highlightIndex={highlightIndex}
              query={query}
              searchable={searchable}
              searchPlaceholder={searchPlaceholder}
              floatingStyle={floatingStyle}
              maxHeight={panelMaxHeight}
              placement={mobileSheet ? 'bottom' : floatingRect?.side === 'top' ? 'top' : 'bottom'}
              mobileSheet={mobileSheet}
              renderEmpty={renderEmpty}
              onKeyDown={onKeyDown}
              onQueryChange={setQuery}
              onOptionHover={setHighlightIndex}
              onOptionClick={toggleRow}
              panelRef={setPanelRef}
              searchInputRef={searchInputRef}
            />,
            portalRoot,
          )
        : null}
    </div>
  );
}
